import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Whether the sidebar is open, after the HyperTek prototype's hook (h-prototype
 * `src/hooks/use-sidebar-state.ts`), with the owner's rules for this product on top:
 *
 * - DESKTOP (≥ lg, 1024 px): the sidebar is always there; "open" expands it from 56 px (icons) to 216 px (icons
 *   and labels). The default is COLLAPSED at every width — the prototype opens by default above 1368 px, this
 *   product does not. The person's own choice is remembered in localStorage and restored.
 * - MOBILE (< lg): "open" is the off-canvas drawer. It is always closed on load — a remembered "open" is a
 *   desktop preference and is never restored into a phone's drawer — and closing it is not remembered either.
 * - The prototype's auto-collapse stays, at a lower width: on a desktop, a window made 1279 px or narrower
 *   collapses the sidebar and remembers that. It only ever collapses, so it cannot fight the collapsed default. It reacts to a
 *   change of WIDTH only: a phone's toolbar sliding away fires `resize` too, and must not close anything.
 *
 * The prototype starts closed and restores in an effect, with a double-rAF `isMounted` flag to hold the
 * transition back through hydration. This is a client-rendered SPA with nothing to hydrate, so the saved
 * value is read in the state initialiser instead: the first paint is already the right width, and there is
 * no transition to suppress.
 *
 * jsdom has no `matchMedia` and storage can be blocked, so every call to either is guarded; without a media
 * query to ask, the viewport counts as mobile (the drawer), which is also the safe answer.
 */

const STORAGE_KEY = 'h-operation-sidebar-open';
/**
 * The auto-collapse width. The prototype's was 1368; with the expanded sidebar down to 216 px a 1366 laptop keeps
 * its labels if its person wants them (1150 px of content), so only a window of 1279 px or less collapses.
 */
const COLLAPSE_BREAKPOINT = 1279;
/** Tailwind's `lg`: where the rail stops being a drawer. */
const DESKTOP_QUERY = '(min-width: 1024px)';

function desktopQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(DESKTOP_QUERY);
}

/** True from 1024 px up, where the sidebar is a bar beside the content rather than a drawer. */
export function isDesktopViewport(): boolean {
  return desktopQuery()?.matches ?? false;
}

function readSaved(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Blocked storage (private mode, a locked-down profile): no preference, so the collapsed default.
    return null;
  }
}

function writeSaved(open: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(open));
  } catch {
    // Not remembered, still applied for this tab — the honest fallback.
  }
}

/** Open only on a desktop whose person chose "open" last time. Everything else starts collapsed. */
export function initialSidebarOpen(): boolean {
  return isDesktopViewport() && readSaved() === 'true';
}

export function useSidebarState() {
  const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
  const lastWidth = useRef(typeof window === 'undefined' ? 0 : window.innerWidth);

  useEffect(() => {
    const onResize = () => {
      const width = window.innerWidth;
      if (width === lastWidth.current) return;
      lastWidth.current = width;
      if (isDesktopViewport() && width <= COLLAPSE_BREAKPOINT) {
        setSidebarOpen(false);
        writeSaved(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Crossing 1024 px (a tablet turned, a window dragged) switches between a drawer and a rail: the drawer
  // starts closed again, the rail gets the person's saved preference back.
  useEffect(() => {
    const query = desktopQuery();
    if (query === null) return undefined;
    const onChange = () => setSidebarOpen(initialSidebarOpen());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const toggleSidebar = useCallback(() => {
    const next = !sidebarOpen;
    setSidebarOpen(next);
    // Only a desktop choice is a preference; opening a phone's drawer is not something to come back to.
    if (isDesktopViewport()) writeSaved(next);
  }, [sidebarOpen]);

  /** Closes the drawer on a phone; a no-op on a desktop, where the width is the person's choice. */
  const closeMobileDrawer = useCallback(() => {
    if (!isDesktopViewport()) setSidebarOpen(false);
  }, []);

  return { sidebarOpen, toggleSidebar, closeMobileDrawer };
}
